/**
 * FeatureGate — renders children only when the current user's plan includes
 * the requested feature (or minPlan level). Otherwise renders an upgrade prompt.
 *
 * Props:
 *   feature    — feature key from FEATURE_DEFINITIONS (optional; use minPlan alone if preferred)
 *   minPlan    — minimum plan required: 'free' | 'starter' | 'professional' | 'enterprise'
 *   fallback   — custom locked-state UI (defaults to inline UpgradePrompt)
 *   silent     — if true, render nothing when locked (no prompt shown)
 *   children   — content to render when the user has access
 */
import React from 'react';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { PLAN_DISPLAY, FEATURE_DEFINITIONS } from '../../config/planFeatures';
import UpgradePrompt from './UpgradePrompt';

const FeatureGate = ({ feature, minPlan, fallback, silent = false, children }) => {
  const { getFeatureAccess } = usePlanLimits();

  // Determine the minimum plan required
  const requiredPlan = minPlan
    || FEATURE_DEFINITIONS.find((f) => f.key === feature)?.minPlan
    || 'free';

  const access = getFeatureAccess(requiredPlan, feature);
  if (access.loading) return null;
  if (access.allowed) return children;

  // Locked state
  if (silent) return null;
  if (fallback) return fallback;

  const featureDef  = FEATURE_DEFINITIONS.find((f) => f.key === feature);
  const planDisplay = PLAN_DISPLAY[requiredPlan] || PLAN_DISPLAY.professional;

  return (
    <UpgradePrompt
      title={featureDef ? `${featureDef.label} requires ${planDisplay.label}` : `Upgrade to ${planDisplay.label}`}
      description={featureDef?.description || `This feature is available on the ${planDisplay.label} plan and above.`}
      requiredPlan={requiredPlan}
    />
  );
};

export default FeatureGate;
